/**
 * SweetAlert2 Configuration and Global Notification Functions
 *
 * This module configures SweetAlert2 with DripScribe theming and provides
 * global notification functions.
 *
 * @module components/swal-config
 */

/**
 * SweetAlert2 themed mixin with DripScribe styling
 */
let swalTheme = null;

/**
 * Initialize SweetAlert2 with theme configuration.
 * Called automatically when Swal is available.
 */
function initSwalTheme() {
  if (typeof Swal === 'undefined') {
    console.warn('[swal-config] SweetAlert2 not loaded');
    return null;
  }

  swalTheme = Swal.mixin({
    customClass: {
      confirmButton: 'btn btn-primary mx-2',
      cancelButton: 'btn btn-secondary mx-2',
      popup: 'shadow-lg',
      title: 'fw-bold',
      htmlContainer: 'text-muted'
    },
    buttonsStyling: false,
    confirmButtonColor: '#6366f1',
    cancelButtonColor: '#6c757d',
  });

  return swalTheme;
}

/**
 * Get the themed Swal instance.
 * @returns {Object} Themed Swal mixin
 */
export function getSwalTheme() {
  if (!swalTheme) {
    swalTheme = initSwalTheme();
  }
  return swalTheme;
}

/**
 * Show a notification toast.
 *
 * @param {string} type - Notification type: 'success', 'error', 'warning', 'info'
 * @param {string} message - Message to display
 * @param {string|null} title - Optional custom title
 */
export function showNotification(type, message, title = null) {
  const theme = getSwalTheme();
  if (!theme) return;

  const icons = {
    'success': 'success',
    'error': 'error',
    'warning': 'warning',
    'info': 'info'
  };

  const titles = {
    'success': 'Success!',
    'error': 'Error!',
    'warning': 'Warning!',
    'info': 'Info'
  };

  theme.fire({
    icon: icons[type] || 'info',
    title: title || titles[type] || 'Notice',
    text: message,
    timer: type === 'success' ? 3000 : null,
    timerProgressBar: type === 'success',
    showConfirmButton: type !== 'success',
  });
}

/**
 * Show a confirmation dialog.
 *
 * @param {string} title - Dialog title
 * @param {string} text - Dialog text
 * @param {string} confirmText - Confirm button text
 * @param {string} cancelText - Cancel button text
 * @param {string} icon - Dialog icon
 * @returns {Promise} SweetAlert2 result promise
 */
export function showConfirm(title, text, confirmText = 'Yes', cancelText = 'Cancel', icon = 'warning') {
  const theme = getSwalTheme();
  if (!theme) return Promise.resolve({ isConfirmed: false });

  return theme.fire({
    title: title,
    text: text,
    icon: icon,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    reverseButtons: true
  });
}

/**
 * Show a loading indicator.
 *
 * @param {string} title - Loading title
 * @param {string} text - Loading text
 */
export function showLoading(title = 'Processing...', text = 'Please wait while we process your request') {
  const theme = getSwalTheme();
  if (!theme) return;

  theme.fire({
    title: title,
    text: text,
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => {
      Swal.showLoading();
    }
  });
}

/**
 * Close any open alert.
 */
export function closeAlert() {
  if (typeof Swal !== 'undefined') {
    Swal.close();
  }
}

/**
 * Export campaign emails with confirmation dialog.
 *
 * @param {number} campaignId - Campaign ID
 * @param {string} exportUrl - URL for export endpoint
 */
export async function exportCampaignEmails(campaignId, exportUrl) {
  const theme = getSwalTheme();
  if (!theme) {
    window.location.href = exportUrl;
    return;
  }

  const result = await theme.fire({
    icon: 'question',
    title: 'Export Emails',
    html: `
      <div class="text-start">
        <p>Export all campaign emails to an Excel file?</p>
        <p class="small text-muted mt-2">
          <i class="fas fa-file-excel text-success me-1"></i>
          The file will include all email content, subject lines, and contact information.
        </p>
      </div>
    `,
    confirmButtonText: '<i class="fas fa-download me-1"></i>Export as Excel',
    cancelButtonText: 'Cancel',
    showCancelButton: true,
    reverseButtons: true
  });

  if (result.isConfirmed) {
    showLoading('Generating Export', 'Creating your Excel file...');
    window.location.href = exportUrl;

    setTimeout(() => {
      closeAlert();
      showNotification('success', 'Your Excel file has been generated and should be downloading.');
    }, 2000);
  }
}

// Initialize on load
document.addEventListener('DOMContentLoaded', () => {
  initSwalTheme();

  // Expose functions globally for backward compatibility
  window.swalTheme = swalTheme;
  window.showNotification = showNotification;
  window.showConfirm = showConfirm;
  window.showLoading = showLoading;
  window.closeAlert = closeAlert;
  window.exportCampaignEmails = exportCampaignEmails;
});

// Also expose via DripScribe namespace
if (typeof window !== 'undefined') {
  window.DripScribe = window.DripScribe || {};
  window.DripScribe.swal = {
    getSwalTheme,
    showNotification,
    showConfirm,
    showLoading,
    closeAlert,
    exportCampaignEmails,
  };
}

export default {
  getSwalTheme,
  showNotification,
  showConfirm,
  showLoading,
  closeAlert,
  exportCampaignEmails,
};
